import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Gauge, Target, LogOut, Settings, UsersRound, TrendingUp, Menu, X, Download, Share, CheckCircle2, Maximize2, Minimize2, RefreshCw, BarChart3, Flame, GraduationCap, Landmark } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useDashboardVisibility } from "../lib/visibility";
import { usePWAInstall } from "../hooks/usePWAInstall";
import api from "../lib/api";

export default function AppShell({ children }) {
  const { logout, user } = useAuth();
  const { flags, isAdmin } = useDashboardVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const [health, setHealth] = useState({ crm_overview: null, leads_performance: null });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const { canInstall, install, installed, platform } = usePWAInstall();

  // Fullscreen mode — hides sidebar and enters browser fullscreen so the
  // dashboard fills the whole viewport (perfect for TV displays / boardroom
  // reviews). Uses the standard `requestFullscreen` API; if it throws (e.g.
  // Safari without user gesture) we still apply the sidebar-hidden layout so
  // the commercial team gets the wide-canvas benefit anyway.
  const toggleFullscreen = async () => {
    const next = !fullscreen;
    setFullscreen(next);
    try {
      if (next && document.fullscreenElement == null) {
        await document.documentElement.requestFullscreen?.();
      } else if (!next && document.fullscreenElement != null) {
        await document.exitFullscreen?.();
      }
    } catch (_e) { /* ignored — layout class still applied */ }
  };

  // Keep local state in sync when the user exits fullscreen via ESC or
  // OS controls so the button icon and layout collapse cleanly back.
  useEffect(() => {
    const sync = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  // Auto-refresh mode — reloads the whole app every 5 minutes so dashboards
  // running on TV displays / boardroom monitors always show fresh KPIs
  // without human intervention. Preference persists in localStorage so it
  // survives the very reloads it triggers.
  const REFRESH_MS = 5 * 60 * 1000;
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem("sdm_auto_refresh") === "1"; }
    catch { return false; }
  });
  const [refreshTick, setRefreshTick] = useState(REFRESH_MS);

  useEffect(() => {
    try { localStorage.setItem("sdm_auto_refresh", autoRefresh ? "1" : "0"); } catch { /* ignore quota errors */ }
    if (!autoRefresh) return;
    // Countdown ticker for the visible label (updates every 1s).
    setRefreshTick(REFRESH_MS);
    const started = Date.now();
    const tick = setInterval(() => {
      const remaining = Math.max(0, REFRESH_MS - (Date.now() - started));
      setRefreshTick(remaining);
      if (remaining === 0) {
        window.location.reload();
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [autoRefresh]);

  const refreshCountdown = (() => {
    if (!autoRefresh) return "5min";
    const s = Math.round(refreshTick / 1000);
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, "0")}`;
  })();

  // "Actualizado hace…" — resets whenever the route changes OR the app
  // reloads (auto-refresh triggers a full reload). Gives the operator on the
  // TV / boardroom a confidence signal: how stale is the data on screen.
  const [lastLoadedAt, setLastLoadedAt] = useState(() => Date.now());
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    setLastLoadedAt(Date.now());
  }, [location.pathname]);
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const lastLoadedLabel = (() => {
    const s = Math.max(0, Math.floor((nowTick - lastLoadedAt) / 1000));
    if (s < 5)     return "recién ahora";
    if (s < 60)    return `hace ${s}s`;
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    if (mm < 60)   return `hace ${mm}m ${String(ss).padStart(2, "0")}s`;
    const hh = Math.floor(mm / 60);
    return `hace ${hh}h ${mm % 60}m`;
  })();

  // Admins always see every nav item; non-admins honour the saved flags.
  const showItem = (key) => isAdmin || flags[key] !== false;

  useEffect(() => {
    const fetchHealth = () => {
      api.get("/dashboards/health").then((r) => setHealth(r.data || {})).catch(() => {});
    };
    fetchHealth();
    const iv = setInterval(fetchHealth, 60000);
    return () => clearInterval(iv);
  }, []);

  // Close drawer on route change (feels natural on mobile).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = (
    <>
      {showItem("sales_performance") && (
        <NavLink to="/sales-performance" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-sales-performance">
          <BarChart3 size={18} strokeWidth={2.2}/>
          <span>Sales Performance</span>
        </NavLink>
      )}
      {showItem("crm_overview") && (
        <NavLink to="/crm-overview" end className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-crm">
          <LayoutDashboard size={18} strokeWidth={2.2}/>
          <span>CRM Overview</span>
          <HealthDot status={health.crm_overview?.status} message={health.crm_overview?.message} testid="health-crm"/>
        </NavLink>
      )}
      {showItem("leads_performance") && (
        <NavLink to="/leads-performance" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-leads-performance">
          <Target size={18} strokeWidth={2.2}/>
          <span>Lead Performance</span>
          <HealthDot status={health.leads_performance?.status} message={health.leads_performance?.message} testid="health-leads"/>
        </NavLink>
      )}
      {showItem("predictive") && (
        <NavLink to="/predictive" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-predictive">
          <TrendingUp size={18} strokeWidth={2.2}/>
          <span>Forecast e Inventario</span>
        </NavLink>
      )}
      {showItem("scorecard") && (
        <NavLink to="/scorecard" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-scorecard">
          <Gauge size={18} strokeWidth={2.2}/>
          <span>Scorecard ND</span>
        </NavLink>
      )}
      {showItem("zonas_hot") && (
        <NavLink to="/zonas-hot" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-zonas-hot">
          <Flame size={18} strokeWidth={2.2}/>
          <span>Zonas Hot SDM</span>
        </NavLink>
      )}
      {(isAdmin || flags.fni === true) && (
        <NavLink to="/fni" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-fni">
          <Landmark size={18} strokeWidth={2.2}/>
          <span>F&amp;I</span>
        </NavLink>
      )}
      {user?.role === "admin" && (
        <NavLink to="/admin/integrations" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-admin">
          <Settings size={18} strokeWidth={2.2}/>
          <span>Integraciones</span>
        </NavLink>
      )}
      {user?.role === "admin" && (
        <NavLink to="/admin/users" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-admin-users">
          <UsersRound size={18} strokeWidth={2.2}/>
          <span>Usuarios y Roles</span>
        </NavLink>
      )}
      <NavLink to="/entrenamiento" className={({ isActive }) => `sidebar-link${isActive ? " is-active" : ""}`} data-testid="nav-training">
        <GraduationCap size={18} strokeWidth={2.2}/>
        <span>Entrenamiento</span>
      </NavLink>
      {canInstall && (
        <button
          type="button"
          className="sidebar-link sidebar-link--install"
          onClick={() => {
            if (platform === "native") install();
            else setShowIOSHelp((v) => !v);
          }}
          data-testid="nav-install-app"
          title="Instalar como aplicación"
        >
          <Download size={18} strokeWidth={2.2}/>
          <span>Instalar app</span>
          <span className="sidebar-link__badge" aria-hidden="true">Nuevo</span>
        </button>
      )}
      {installed && (
        <div
          className="sidebar-installed-pill"
          data-testid="sidebar-app-installed"
          title="Estás usando la versión instalada de SDM BI"
        >
          <CheckCircle2 size={13} strokeWidth={2.4}/>
          <span>App activa</span>
        </div>
      )}
    </>
  );

  const presentationControls = (
    <div className="sidebar-presentation">
      <span
        className="app-last-updated"
        title="Tiempo desde la última carga o cambio de vista"
        data-testid="app-last-updated"
      >
        <span className="app-last-updated__dot" aria-hidden="true"/>
        <span className="app-last-updated__label">
          Actualizado <strong>{lastLoadedLabel}</strong>
        </span>
      </span>
      <div className="sidebar-presentation__row">
        <button
          type="button"
          className={`app-refresh-toggle${autoRefresh ? " is-active" : ""}`}
          onClick={() => setAutoRefresh((v) => !v)}
          aria-label={autoRefresh ? `Auto-refresh activo — próxima recarga en ${refreshCountdown}` : "Activar auto-refresh cada 5 minutos"}
          title={autoRefresh ? `Auto-refresh activo · próxima recarga en ${refreshCountdown}` : "Auto-refresh cada 5 minutos"}
          data-testid="app-refresh-toggle"
        >
          <RefreshCw size={14} className={autoRefresh ? "app-refresh-icon-spin" : ""}/>
          <span className="app-refresh-toggle__label">{refreshCountdown}</span>
        </button>
        <button
          type="button"
          className="app-fullscreen-toggle"
          onClick={toggleFullscreen}
          aria-label={fullscreen ? "Salir de pantalla completa" : "Modo pantalla completa"}
          title={fullscreen ? "Salir de pantalla completa (ESC)" : "Modo pantalla completa"}
          data-testid="app-fullscreen-toggle"
        >
          {fullscreen ? <Minimize2 size={16}/> : <Maximize2 size={16}/>}
        </button>
      </div>
    </div>
  );

  const userFooter = (
    <div className="sidebar-footer">
      {presentationControls}
      <div className="sidebar-user" title={user?.email}>
        <div className="avatar">{(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}</div>
        <div className="meta">
          <div className="name">
            {user?.name || "Usuario"}
            {user?.role && (
              <span className="pill ml-1.5" style={{
                fontSize: 9, fontWeight: 800, letterSpacing: "0.04em", padding: "1px 6px",
                background: user.role === "admin" ? "#fee2e2" : "#dcfce7",
                color:      user.role === "admin" ? "#991b1b" : "#166534",
              }} data-testid="user-role-badge">{user.role.toUpperCase()}</span>
            )}
          </div>
          <div className="email">{user?.email}</div>
        </div>
      </div>
      <button className="sidebar-logout" onClick={handleLogout} data-testid="sidebar-logout">
        <LogOut size={14}/> Salir
      </button>
    </div>
  );

  return (
    <div className={`app-shell${fullscreen ? " is-fullscreen" : ""}`} data-testid="app-shell">
      {/* In fullscreen mode the sidebar is hidden, so the presentation
          controls float top-right; in normal mode they live inside the
          sidebar footer (see `presentationControls`). */}
      {fullscreen && (
        <div className="app-presentation-controls app-presentation-controls--floating">
          <span
            className="app-last-updated"
            title="Tiempo desde la última carga o cambio de vista"
            data-testid="app-last-updated-fs"
          >
            <span className="app-last-updated__dot" aria-hidden="true"/>
            Actualizado <strong>{lastLoadedLabel}</strong>
          </span>
          <button
            type="button"
            className={`app-refresh-toggle${autoRefresh ? " is-active" : ""}`}
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh ? `Auto-refresh · próxima recarga en ${refreshCountdown}` : "Auto-refresh cada 5 minutos"}
            data-testid="app-refresh-toggle-fs"
          >
            <RefreshCw size={14} className={autoRefresh ? "app-refresh-icon-spin" : ""}/>
            <span className="app-refresh-toggle__label">{refreshCountdown}</span>
          </button>
          <button
            type="button"
            className="app-fullscreen-toggle"
            onClick={toggleFullscreen}
            title="Salir de pantalla completa (ESC)"
            data-testid="app-fullscreen-toggle-fs"
          >
            <Minimize2 size={16}/>
          </button>
        </div>
      )}

      {/* Mobile top-bar with hamburger — hidden on desktop via CSS */}
      <header className="app-topbar" data-testid="app-topbar">
        <button
          className="app-topbar__menu"
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir menú"
          data-testid="app-topbar-menu"
        >
          <Menu size={22}/>
        </button>
        <a
          href="https://www.santodomingomotors.com.do"
          target="_blank"
          rel="noopener noreferrer"
          className="app-topbar__brand"
          aria-label="Santo Domingo Motors"
        >
          <img src="/brand/sdm-logo.svg" alt="SDM" />
        </a>
        <button
          className="app-topbar__logout"
          onClick={handleLogout}
          aria-label="Salir"
          data-testid="app-topbar-logout"
        >
          <LogOut size={16}/>
        </button>
      </header>

      {/* Desktop sidebar — always in DOM; hidden on mobile via CSS */}
      <aside className="app-sidebar" data-testid="app-sidebar">
        <a
          href="https://www.santodomingomotors.com.do"
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar-brand"
          data-testid="sidebar-brand-logo"
          aria-label="Santo Domingo Motors"
        >
          <img src="/brand/sdm-logo.svg" alt="SDM" />
        </a>
        <nav className="sidebar-nav">
          {navItems}
        </nav>
        {userFooter}
      </aside>

      {/* Mobile drawer — only visible when opened on small screens */}
      {drawerOpen && (
        <>
          <div className="app-drawer__backdrop"
               onClick={() => setDrawerOpen(false)}
               data-testid="app-drawer-backdrop"/>
          <aside className="app-drawer" data-testid="app-drawer">
            <div className="app-drawer__header">
              <img src="/brand/sdm-logo.svg" alt="SDM" className="app-drawer__logo"/>
              <button
                className="app-drawer__close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Cerrar menú"
                data-testid="app-drawer-close"
              >
                <X size={22}/>
              </button>
            </div>
            <nav className="sidebar-nav">
              {navItems}
            </nav>
            {userFooter}
          </aside>
        </>
      )}

      <main className="app-main">{children}</main>

      {/* iOS Add-to-Home-Screen instructions — modal fired from the sidebar
          "Instalar app" button. Only reachable when the browser has no
          `beforeinstallprompt` support (Safari on iOS). */}
      {showIOSHelp && (
        <div
          className="ios-install-modal__backdrop"
          onClick={() => setShowIOSHelp(false)}
          data-testid="ios-install-modal-backdrop"
        >
          <div
            className="ios-install-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Instalar en iOS"
            data-testid="ios-install-modal"
          >
            <button
              className="ios-install-modal__close"
              onClick={() => setShowIOSHelp(false)}
              aria-label="Cerrar"
            >
              <X size={18}/>
            </button>
            <img src="/brand/sdm-icon-192.png" alt="" className="ios-install-modal__icon"/>
            <h3 className="ios-install-modal__title">Instalar SDM BI en iPhone</h3>
            <ol className="ios-install-modal__steps">
              <li>Toca el botón <strong>Compartir</strong> <Share size={14} style={{ verticalAlign: "middle" }}/> en la barra inferior de Safari.</li>
              <li>Desplázate y selecciona <strong>{"Añadir a pantalla de inicio"}</strong>.</li>
              <li>Confirma con <strong>Añadir</strong> — el ícono aparecerá junto a tus apps.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthDot({ status, message, testid }) {
  if (!status) return null;
  const colors = { green: "#16a34a", amber: "#f59e0b", red: "#dc2626" };
  const labels = { green: "Activo", amber: "Atención", red: "Crítico" };
  const c = colors[status] || "#6b7280";
  return (
    <span
      className={`health-dot health-${status}`}
      style={{ background: c }}
      data-testid={testid}
      data-status={status}
      title={`${labels[status] || "—"} · ${message || ""}`}
      aria-label={`${labels[status]}: ${message || ""}`}
    />
  );
}
